import { useMemo, useState } from 'react';

import { Cancel01Icon, ThumbsDownIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { FieldTypes } from 'constants/field-types-enum';
import { FilesFolders } from 'constants/files-folders-enum';
import { SPENT_CONVICTIONS_LEGISLATION_LINKS } from 'constants/static-constants';
import { useToast } from 'hooks/toast';
import { usePortalUploadActions } from 'hooks/upload-actions';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Form } from 'components/Form/Form/Form';
import { FormMessage } from 'components/FormComponents';
import { Loading } from 'components/Loading/Loading';

import { useGetDashboardQuery, useSubmitCheckDisputeMutation } from 'api/candidate-portal-api';
import { selectPortalAuth } from 'api/candidate-portal-slice';

import CandidatePortalPage from './CandidatePortalPage/CandidatePortalPage';

const CONSENT_FIELD_ID = 'dispute_consent';
const SPENT_CONVICTIONS_CLAIM_TYPE = 'type_4';
const SPENT_CONVICTIONS_INFO_FIELD_ID = 'spent_convictions_legislation_info';
const SPENT_CONVICTIONS_CONSENT_FIELD_ID = 'spent_convictions_consent';

const CandidateCheckDispute = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showToast = useToast();
  const { candidateId, candidateCheckId } = useParams();
  const { isAuthenticated, checked } = useSelector(selectPortalAuth);

  const [submitting, setSubmitting] = useState(false);
  const [liveValues, setLiveValues] = useState({});

  const { data: dashboard, isLoading: isLoadingDashboard } = useGetDashboardQuery(undefined, {
    skip: !checked || !isAuthenticated || !candidateId,
  });

  const [submitCheckDispute] = useSubmitCheckDisputeMutation();
  const { getUpload, signUpload, createUpload, deleteUpload } = usePortalUploadActions();

  const dashboardPath = `/portal/dashboard/${candidateId}`;
  const brand = dashboard?.brand;
  const candidate = dashboard?.candidate;

  const formBrand = useMemo(
    () =>
      brand
        ? {
            ...brand,
            brand_logo: brand.logo_url,
            brand_name: brand.name,
          }
        : {},
    [brand],
  );

  const brandStyle = brand?.color_button
    ? {
        '--brand-button-bg': brand.color_button,
        '--brand-button-text': brand.color_button_text,
        '--brand-link-color': brand.color_link,
      }
    : {};

  const checkDetails = useMemo(() => {
    if (!dashboard?.checks || !candidateCheckId) return null;
    return dashboard.checks.find(check => String(check.id) === String(candidateCheckId)) || null;
  }, [dashboard, candidateCheckId]);

  const showSpentConvictionsPrompt =
    liveValues?.dispute_claim_type === SPENT_CONVICTIONS_CLAIM_TYPE;

  const spentConvictionsLegislationHtml = useMemo(() => {
    const items = SPENT_CONVICTIONS_LEGISLATION_LINKS.map(
      ({ jurisdiction, act, url }) =>
        `<li><a href="${url}" target="_blank" rel="noopener noreferrer"><b>${jurisdiction}</b> — ${act}</a></li>`,
    ).join('');
    return `<p>${t('candidatePortal.dispute.fields.spentConvictionsIntro')}</p><ul style="margin: 0.5rem 0 0 1rem; padding-left: 0.75rem;">${items}</ul>`;
  }, [t]);

  const fields = useMemo(() => {
    const baseFields = [
      {
        id: 'dispute_claim_type',
        type: FieldTypes.MultipleChoice,
        text: t('candidatePortal.dispute.fields.claimType'),
        required: true,
        options: {
          multipleSelection: false,
          options: [
            { value: 'type_1', name: t('candidatePortal.dispute.fields.claimTypeOption1') },
            { value: 'type_2', name: t('candidatePortal.dispute.fields.claimTypeOption2') },
            { value: 'type_3', name: t('candidatePortal.dispute.fields.claimTypeOption3') },
            {
              value: SPENT_CONVICTIONS_CLAIM_TYPE,
              name: t('candidatePortal.dispute.fields.claimTypeOption4'),
            },
            { value: 'type_5', name: t('candidatePortal.dispute.fields.claimTypeOption5') },
          ],
        },
      },
    ];

    if (showSpentConvictionsPrompt) {
      baseFields.push({
        id: SPENT_CONVICTIONS_INFO_FIELD_ID,
        type: FieldTypes.Statement,
        title: t('candidatePortal.dispute.fields.spentConvictionsTitle'),
        text: spentConvictionsLegislationHtml,
      });
    }

    baseFields.push(
      {
        id: 'disputed_police_information',
        type: FieldTypes.FreeText,
        text: t('candidatePortal.dispute.fields.disputedPoliceInformation'),
        description: t('candidatePortal.dispute.fields.disputedPoliceInformationDescription'),
        required: true,
      },
      {
        id: 'additional_info_evidence',
        type: FieldTypes.FreeText,
        text: t('candidatePortal.dispute.fields.additionalInfoEvidence'),
        description: t('candidatePortal.dispute.fields.additionalInfoEvidenceDescription'),
        required: false,
      },
      {
        id: 'evidence_documents',
        type: FieldTypes.MultiUpload,
        text: t('candidatePortal.dispute.fields.evidenceDocuments'),
        description: t('candidatePortal.dispute.fields.evidenceDocumentsDescription'),
        accept: {
          'application/pdf': ['.pdf'],
          'image/*': [],
        },
        metadata: {
          check_id: checkDetails?.id,
          dispute_form: true,
          bucket_folder: FilesFolders.Acic,
        },
      },
      {
        id: 'resided_at_address',
        type: FieldTypes.Address,
        text: t('candidatePortal.dispute.fields.residedAddress'),
        required: false,
        defaultValue: 'AU',
        validatorName: 'addressInCountry',
        validationParams: {
          country: 'AU',
          errorMessage: t('candidatePortal.dispute.validation.residedAddressAustralia'),
        },
      },
      {
        id: 'resided_from_date',
        type: FieldTypes.Date,
        text: t('candidatePortal.dispute.fields.residedFromDate'),
        required: false,
        useDefaultToday: false,
      },
      {
        id: 'resided_to_date',
        type: FieldTypes.Date,
        text: t('candidatePortal.dispute.fields.residedToDate'),
        required: false,
        useDefaultToday: false,
        validatorName: 'dateStrictlyAfter',
        validationParams: {
          dependsOnFields: ['resided_from_date'],
          errorMessage: t('candidatePortal.dispute.validation.residedToAfterFrom'),
        },
      },
    );

    if (showSpentConvictionsPrompt) {
      baseFields.push({
        id: SPENT_CONVICTIONS_CONSENT_FIELD_ID,
        type: FieldTypes.Consent,
        text: t('candidatePortal.dispute.fields.spentConvictionsConsentLabel'),
        required: true,
      });
    }

    baseFields.push({
      id: CONSENT_FIELD_ID,
      type: FieldTypes.Consent,
      text: t('candidatePortal.dispute.fields.consentLabel'),
      description: t('candidatePortal.dispute.fields.consentDescription'),
      required: true,
    });

    return baseFields;
  }, [t, checkDetails, showSpentConvictionsPrompt, spentConvictionsLegislationHtml]);

  const goToDashboard = () => navigate(dashboardPath);

  const handleSubmit = async data => {
    setSubmitting(true);
    const values = { ...data };

    delete values[CONSENT_FIELD_ID];
    values.dispute_consent_label = t('candidatePortal.dispute.fields.consentLabel');

    if (values[SPENT_CONVICTIONS_CONSENT_FIELD_ID]) {
      values.spent_convictions_consent_label = t(
        'candidatePortal.dispute.fields.spentConvictionsConsentLabel',
      );
    }
    delete values[SPENT_CONVICTIONS_CONSENT_FIELD_ID];
    delete values[SPENT_CONVICTIONS_INFO_FIELD_ID];

    try {
      await submitCheckDispute({ candidateCheckId, values }).unwrap();
      showToast(
        t('candidatePortal.dispute.successTitle'),
        t('candidatePortal.dispute.successMessage'),
        'success',
      );
      navigate(dashboardPath);
    } catch (error) {
      setSubmitting(false);
      showToast(
        t('candidatePortal.dispute.errorTitle'),
        error?.data?.error?.log || t('candidatePortal.dispute.errorMessage'),
        'error',
      );
    }
  };

  const isLoading = !checked || isLoadingDashboard;

  if (isLoading) {
    return (
      <CandidatePortalPage showFooter={false} showHeader={false}>
        <Loading active />
      </CandidatePortalPage>
    );
  }

  if (!checkDetails) {
    return (
      <CandidatePortalPage candidate={candidate} brand={brand} showFooter={false}>
        <div className="container px-4 py-8">
          <FormMessage message={t('candidatePortal.dispute.errorMessage')} />
          <button
            type="button"
            onClick={goToDashboard}
            className="mt-4 text-xl font-medium text-text-primary underline bg-transparent border-0 cursor-pointer">
            {t('common.cancel')}
          </button>
        </div>
      </CandidatePortalPage>
    );
  }

  return (
    <CandidatePortalPage candidate={candidate} brand={brand} showFooter={false} showHeader={false}>
      <Loading active={submitting} />
      <div
        className="fixed inset-0 z-[60] flex flex-col bg-white overflow-y-auto"
        style={brandStyle}>
        <header className="shrink-0 h-20 px-6 sm:px-8 bg-white flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            {brand?.logo_url ? (
              <img
                src={brand.logo_url}
                alt={brand.name || ''}
                className="h-10 w-auto max-w-[140px] object-contain"
              />
            ) : (
              <img
                src="/assets/images/checkmate/icon-label.svg"
                alt={t('candidatePortal.checkmateLogo')}
                className="h-8 w-auto"
              />
            )}
          </div>
          <button
            type="button"
            onClick={goToDashboard}
            aria-label={t('common.close')}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border-primary bg-transparent text-text-primary text-xl font-medium cursor-pointer hover:bg-surface-secondary transition-colors shrink-0">
            <HugeiconsIcon icon={Cancel01Icon} size={18} />
            {t('common.cancel')}
          </button>
        </header>

        <div className="flex-1 min-h-0 px-6 sm:px-8 py-8 max-w-5xl w-full mx-auto">
          <Form
            header={true}
            headerTitle={t('candidatePortal.dispute.title')}
            headerIcon={ThumbsDownIcon}
            headerIconSize="xlarge"
            form={{ fields }}
            candidateId={candidate?.id}
            country={dashboard?.company?.country}
            brand={formBrand}
            getCandidateUpload={getUpload}
            signCandidateUpload={signUpload}
            createCandidateUpload={createUpload}
            deleteCandidateUpload={deleteUpload}
            buttonTitle={t('candidatePortal.dispute.submit')}
            onSubmit={handleSubmit}
            onValuesChange={setLiveValues}
            fieldRequiredVariantStyle="text"
            className="pb-32"
          />
        </div>
      </div>
    </CandidatePortalPage>
  );
};

export default CandidateCheckDispute;
